import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { API } from "@/lib/constants";
import { useAdminAuth, getAdminHeaders, formatDate } from "@/lib/admin";
import { Button } from "@/components/ui/button";

export default function AdminContactsPage({ language }) {
  const navigate = useNavigate();
  const authenticated = useAdminAuth(navigate);
  const [contacts, setContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (!authenticated) return;
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/admin/contact-requests`, {
          headers: getAdminHeaders(),
        });
        setContacts(res.data || []);
      } catch (err) {
        console.error("Failed to load contact requests:", err);
      }
      setIsLoading(false);
    };
    fetchData();
  }, [authenticated]);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this request?")) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API}/admin/contact-requests/${id}`, {
        headers: getAdminHeaders(),
      });
      setContacts((prev) => prev.filter((c) => c.id !== id));
    } catch {
      alert("Failed to delete request.");
    }
    setDeletingId(null);
  };

  if (!authenticated) return null;

  return (
    <div className="min-h-screen bg-[#f4f6fb]">
      <Header language={language} />
      <main className="pt-28 pb-20">
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div>
            <Link
              to="/admin-dashboard"
              className="text-xs uppercase tracking-widest text-[#c2873c] hover:text-[#003366] transition-colors"
              data-testid="admin-contacts-back"
            >
              &larr; Back to Dashboard
            </Link>
            <h1
              className="font-heading text-3xl sm:text-4xl text-[#003366] mt-3"
              data-testid="admin-contacts-title"
            >
              Documentation Requests
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              {contacts.length} total request{contacts.length !== 1 ? "s" : ""}
            </p>
          </div>

          <div className="mt-8 space-y-4" data-testid="admin-contacts-list">
            {isLoading ? (
              <p className="text-slate-500 p-6 bg-white border border-slate-200">
                Loading requests...
              </p>
            ) : contacts.length === 0 ? (
              <p
                className="text-slate-500 p-6 bg-white border border-slate-200"
                data-testid="admin-contacts-empty"
              >
                No contact requests yet.
              </p>
            ) : (
              contacts.map((c, index) => (
                <div
                  key={c.id || `${c.email}-${index}`}
                  className="bg-white border border-slate-200 shadow-sm p-6"
                  data-testid={`admin-contact-card-${index}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex items-start gap-4">
                      <span
                        className="flex-shrink-0 w-8 h-8 bg-[#c2873c] text-white text-sm font-bold flex items-center justify-center"
                        data-testid={`admin-contact-sno-${index}`}
                      >
                        {index + 1}
                      </span>
                      <div>
                        <p
                          className="font-heading text-xl text-[#003366]"
                          data-testid={`admin-contact-name-${index}`}
                        >
                          {c.name}
                        </p>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-slate-500">
                          <span>{c.email}</span>
                          <span>{c.phone}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-400 whitespace-nowrap">
                        {formatDate(c.created_at)}
                      </span>
                      <Button
                        variant="outline"
                        className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white rounded-none px-3 py-1 text-xs font-bold uppercase tracking-wider"
                        onClick={() => handleDelete(c.id)}
                        disabled={deletingId === c.id}
                        data-testid={`admin-contact-delete-${index}`}
                      >
                        {deletingId === c.id ? "Deleting..." : "Delete"}
                      </Button>
                    </div>
                  </div>
                  {c.organization && (
                    <div className="mt-3 ml-12">
                      <p className="text-xs uppercase tracking-wider text-slate-400">
                        Organization
                      </p>
                      <p className="mt-1 text-slate-700 font-medium">{c.organization}</p>
                    </div>
                  )}
                  <div className="mt-3 ml-12">
                    <p className="text-xs uppercase tracking-wider text-slate-400">Message</p>
                    <p
                      className="mt-1 text-slate-700 bg-slate-50 border border-slate-200 p-4"
                      data-testid={`admin-contact-message-${index}`}
                    >
                      {c.message}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      </main>
      <Footer language={language} />
    </div>
  );
}
